"use client";

import { Box, Pagination, Typography } from "@mui/material";
import { Trans } from "@lingui/macro";
import React, { useEffect } from "react";
import { useSearchParams } from "next/navigation";

interface PaginationComponentProps {
  totalPages: number;
  currentPage: number;
  itemsToDisplay: number;
  totalItems: number;
  setCurrentPage: (page: number) => void;
  scrollTo?: number;
  persistPage?: boolean;
  triggerScroll?: boolean;
}

const PaginationComponent: React.FC<PaginationComponentProps> = ({
  totalPages,
  currentPage,
  itemsToDisplay,
  totalItems,
  setCurrentPage,
  scrollTo = 0,
  persistPage = true,
  triggerScroll = true,
}) => {
  const startIndex = currentPage > 0 ? (currentPage - 1) * itemsToDisplay : 0;
  const searchParams = useSearchParams();

  useEffect(() => {
    if (persistPage) {
      const params = new URLSearchParams(searchParams.toString());
      if (searchParams.has("page")) {
        const page = searchParams.get("page") ?? "";
        params.delete("page");
        setCurrentPage(parseInt(page));
      } else if (currentPage > 1) {
        setCurrentPage(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handlePageChange = (_event: unknown, page: number) => {
    setCurrentPage(page);
    if (triggerScroll) {
      window.scrollTo({ top: scrollTo, behavior: "smooth" });
    }
    if (persistPage) {
      const params = new URLSearchParams(searchParams.toString());
      if (page > 1) {
        params.set("page", page.toString());
      } else {
        params.delete("page");
      }
      window.history.pushState(null, "", `?${decodeURIComponent(params.toString())}`);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        alignItems: "center",
        justifyContent: "space-between",
        pt: { xs: "40px", md: "20px" },
        pb: "20px",
      }}
    >
      {totalPages > 1 && (
        <Pagination
          variant="outlined"
          shape="rounded"
          page={currentPage}
          count={totalPages}
          onChange={handlePageChange}
          sx={{
            "& .MuiPaginationItem-root:not(.MuiPaginationItem-ellipsis)": {
              bgcolor: "#eee",
              border: "none",
              "&:hover, &.Mui-selected": {
                bgcolor: "primary.main",
                color: "#fff",
              },
            },
          }}
        />
      )}
      <Typography
        sx={{
          ml: { xs: 0, md: "auto" },
          fontSize: { xs: "12px", md: "14px" },
          color: { xs: "rgba(0,0,0,0.5)", md: "#000" },
        }}
      >
        <Trans>
          Showing {startIndex + 1} to{" "}
          {startIndex + itemsToDisplay > totalItems ? totalItems : startIndex + itemsToDisplay} of{" "}
          {totalItems} ({totalPages} Pages)
        </Trans>
      </Typography>
    </Box>
  );
};

export default PaginationComponent;
